import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Plug, Trash } from "@phosphor-icons/react";

export default function Brokers() {
  const [catalog, setCatalog] = useState([]);
  const [conns, setConns] = useState([]);

  const load = async () => {
    const [c, k] = await Promise.all([
      api.get("/brokers/catalog"),
      api.get("/brokers/connections"),
    ]);
    setCatalog(c.data);
    setConns(k.data);
  };

  useEffect(() => { load(); }, []);

  const isConnected = (id) => conns.some((c) => c.broker_id === id);

  const connect = async (id) => {
    try {
      await api.post("/brokers/connect", { broker_id: id });
      toast.success("Broker connected");
      load();
    } catch (e) { toast.error("Failed to connect"); }
  };

  const disconnect = async (cid) => {
    try {
      await api.delete(`/brokers/connections/${cid}`);
      toast.success("Disconnected");
      load();
    } catch (e) { toast.error("Failed to disconnect"); }
  };

  return (
    <Layout>
      <div className="p-6">
        <header className="mb-6">
          <h1 className="text-2xl font-medium tracking-tight">Broker Connections</h1>
          <p className="text-xs text-[#8B95A5] mt-1">
            Connect each broker individually. Demo mode uses simulated balances and orders — no real
            credentials required.
          </p>
        </header>

        {conns.length > 0 && (
          <section className="mb-8" data-testid="connected-section">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8B95A5] mb-3">Active Connections</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {conns.map((c) => (
                <div key={c.id} className="panel p-4" data-testid={`conn-card-${c.broker_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold tracking-tight">{c.display_name}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A5] font-mono">{c.market} · {c.region}</div>
                    </div>
                    <span className={`tag ${c.status === "connected" ? "buy" : "hold"}`}>{c.status}</span>
                  </div>
                  <div className="mt-3 text-xs text-[#8B95A5]">Mock balance</div>
                  <div className="font-mono text-lg">{fmtMoney(c.mock_balance, c.currency)} <span className="text-[10px] text-[#4B5563]">{c.currency}</span></div>
                  <div className="flex gap-2 mt-4">
                    <button
                      className="btn-outline flex-1"
                      onClick={() => disconnect(c.id)}
                      data-testid={`disconnect-${c.broker_id}`}
                    >
                      <Trash size={12} className="mr-1 inline" /> Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section data-testid="catalog-section">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8B95A5] mb-3">Available Platforms</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {catalog.map((b) => (
              <div key={b.id} className="panel p-4 relative overflow-hidden" data-testid={`catalog-card-${b.id}`}>
                <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: b.color }} />
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold tracking-tight">{b.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#8B95A5] font-mono">{b.market} · {b.region}</div>
                  </div>
                  {isConnected(b.id) ? (
                    <span className="tag buy">connected</span>
                  ) : (
                    <span className="tag">available</span>
                  )}
                </div>
                <p className="text-xs text-[#8B95A5] mt-3 mb-4 leading-relaxed">{b.description}</p>
                <button
                  className={isConnected(b.id) ? "btn-outline w-full opacity-50" : "btn-primary w-full"}
                  disabled={isConnected(b.id)}
                  onClick={() => connect(b.id)}
                  data-testid={`connect-${b.id}`}
                >
                  <Plug size={12} className="inline mr-1" /> {isConnected(b.id) ? "Connected" : "Connect"}
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
